import FileUploadFlow from "./FileUploadFlow";
import PageRangeSelector from "./PageRangeSelector";
import { useEffect, useState } from "react";
import usePost from "../services/usePost.js";
import useGet from "../services/useGet.js";
import useFetchDownload from "../services/useFetchDownload.js";
import { useNavigate } from "react-router";
import * as pdfjsLib from "pdfjs-dist";
import useUserName from "../services/useUserName";
import useTrackUserEvent from "../services/useTrackUserEvent";

const Step1 = ({ setStep1Download }) => {
  const instructionText = "Upload Brief to Create Flashcards";
  const [startPage, setStartPage] = useState("");
  const [endPage, setEndPage] = useState("");
  const [maxPage, setMaxPage] = useState();
  const [processing, setProcessing] = useState(false);
  const [complete, setComplete] = useState(false);
  const [file, setFile] = useState();
  const [error, setError] = useState();
  const [isValid, setIsValid] = useState(false);
  const post = usePost();
  const get = useGet();
  const fetchDownload = useFetchDownload();

  const user = useUserName();

  const trackUserEvent = useTrackUserEvent();

  function handleError(errorMessage) {
    setProcessing(false);
    setFile(null);
    setStartPage("");
    setEndPage("");
    setIsValid(false);
    setError(errorMessage);
  }

  useEffect(
    () => { document.title = "Podium Prepper - Upload Brief" },
    []
  );
  useEffect(() => setStep1Download(null), [setStep1Download]);
  useEffect(() => {
    pdfjsLib.GlobalWorkerOptions.workerSrc = "./static/js/pdf.worker.min.mjs"
    if (file && pdfjsLib.isPdfFile(file.name || file.path || "")) {
      var fileReader = new FileReader();

      fileReader.onload = function () {
        var typedarray = new Uint8Array(this.result);

        const loadingTask = pdfjsLib.getDocument(typedarray);
        loadingTask.promise
          .then((pdf) => {
            setStartPage(1);
            setEndPage(pdf.numPages);
            setMaxPage(pdf.numPages);
          })
          .catch((error) => {
            setError(file.name + " was not loadable as a pdf");
            setFile(null);
          });
      };
      fileReader.readAsArrayBuffer(file);
    }
  }, [file]);

  const navigate = useNavigate();

  const ConfirmAction = async (file) => {
    const estimatedProcessingTime = 15 + 5 * (parseInt(endPage) - parseInt(startPage)); // estimate 5 seconds per page plus 15 seconds overhead

    setProcessing({
      estimatedTime: estimatedProcessingTime
    });

    var formdata = new FormData();
    formdata.append("file", file);
    formdata.append("startPage", startPage);
    formdata.append("endPage", endPage);
    formdata.append("user", user);

    trackUserEvent("Submitted brief and page range", {
      file: file.name,
      startPage,
      endPage,
    });

    const beginJobResponse = await post(process.env.REACT_APP_API_ENDPOINT_START_JOB, formdata, handleError);
    if (beginJobResponse.status !== 200) {
      const errorMessage = await beginJobResponse.text();
      handleError(errorMessage);
      return;
    }

    const jobId = await beginJobResponse.text();

    let jobStatus = "running";
    const timeoutInterval = Math.min(5000, estimatedProcessingTime * 1000 / 10); 
    while (jobStatus === "running") {
      await new Promise((r) => setTimeout(r, timeoutInterval));
      const jobStatusResponse = await get(`${process.env.REACT_APP_API_ENDPOINT_JOB_STATUS}?instanceId=${jobId}`, handleError)
      if (jobStatusResponse.status !== 200) {
        const errorMessage = await jobStatusResponse.text();
        handleError(errorMessage);
        return;
      }

      jobStatus = await jobStatusResponse.text();
    }

    if (jobStatus !== "succeeded") {
      handleError("Processing job failed.");
      return;
    }
    
    const downloadUrlResponse = await get(`${process.env.REACT_APP_API_ENDPOINT_JOB_GET_DOWNLOAD_URL}?instanceId=${jobId}`, handleError);
    if (downloadUrlResponse.status !== 200) {
      const errorMessage = await downloadUrlResponse.text();
      handleError(errorMessage);
      return;
    }

    const downloadUrl = await downloadUrlResponse.text();

    try {
      const download = await fetchDownload(downloadUrl);
      setStep1Download(download);
      setComplete(true);
    }
    catch (error) {
      handleError("Download failed: " + error.message);
      return;
    }

  };

  return (
    <>
      <FileUploadFlow
        InstructionalText={instructionText}
        ConfirmContent={
          <PageRangeSelector
            {...{
              maxPage,
              startPage,
              setStartPage,
              endPage,
              setEndPage,
              setIsValid,
            }}
          />
        }
        FileTypes={[".pdf"]}
        ConfirmAction={ConfirmAction}
        ConfirmEnabled={isValid}
        onCancel={() => {
          setStartPage("");
          setEndPage("");
          setIsValid(false);
          trackUserEvent("Cancelled file selection for Step 1");
        }}
        onComplete={() => {
          console.log("navigating");
          navigate("/complete");
        }}
        {...{ processing, complete, error, setError, file, setFile }}
      />
    </>
  );
};

export default Step1;
